import { singleline } from "@core/foundation/string";
import { apiEndpoint, keyApi } from "@core/ports/http";
import { z } from "@core/zod";

import { API_DOMAIN } from "@platform/core";

import { MEMORY_SERVICE } from "../config/index.ts";
import {
  AgentSessionEvent,
  Memory,
  Revision,
  Trigger,
} from "../models/index.ts";

const MemorySnapshotItemSchema = z.object(
  {
    memory: Memory.schema,
    revision: Revision.schema,
    triggers: z.array(Trigger.schema),
  },
  {
    memory: "The memory",
    revision: "The memory's latest revision",
    triggers: "The triggers that inject the memory",
  },
);

export type MemorySnapshotItem = z.infer<typeof MemorySnapshotItemSchema>;

export const memoryKeyApi = keyApi(
  { domain: API_DOMAIN, path: `${MEMORY_SERVICE.LABEL}/key` },
  {
    getMemorySnapshot: apiEndpoint({
      body: z
        .object(
          { knownEtag: z.string().optional() },
          {
            knownEtag: singleline`
              The etag of the snapshot the caller holds, which an unmodified
              answer confirms as current
            `,
          },
        )
        .optional(),
      response: z.discriminatedUnion("modified", [
        z.object(
          { modified: z.literal(false) },
          {
            modified: singleline`
              False: the project's memories have not changed since the snapshot
              the caller's etag tags, so that snapshot is current
            `,
          },
        ),
        z.object(
          {
            etag: z.string(),
            items: z.array(MemorySnapshotItemSchema),
            modified: z.literal(true),
          },
          {
            etag: "The snapshot's etag, to send back as knownEtag",
            items: singleline`
              The snapshot: the project's active memories, each with its latest
              revision and its triggers
            `,
            modified: singleline`
              True: the snapshot the caller's etag tags is not current, or the
              caller sent none, so the items follow
            `,
          },
        ),
      ]),
    }),
    ingestAgentSessionEvent: apiEndpoint({
      body: z.object(
        {
          event: AgentSessionEvent.createSchema.omit({
            principalId: true,
            projectId: true,
          }),
        },
        { event: "The agent-session event to log in the project" },
      ),
      response: z.void(),
    }),
  },
);
